import { DynamicConfig } from '@domain/configuration/types';
import { useGeneratedSalesPortalDynamicConfigQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-dynamic-config.generated';
import { useMemo } from 'react';

interface SalesPortalDynamicConfigLoaderParams {
  businessOrganizationUUID: string;
  supplierUUID: string;
}

interface ChatDynamicConfigMap {
  [chatUUID: string]: DynamicConfig | null;
}

interface SalesPortalDynamicConfigLoaderResult {
  chatDynamicConfigMap: ChatDynamicConfigMap;
  error?: Error;
  loading: boolean;
  supplierDynamicConfig: DynamicConfig | null;
}

/**
 * Loader to fetch dynamic config from the backend for Sales Portal.
 * Fetches supplier config and all chat configs for the business organization.
 *
 * This is the data-fetching layer (Loader) that should be consumed by hooks
 * that need access to dynamic config data.
 *
 * @deprecated Fetches every chat's config for the whole business organization
 * (~3MB for large distributors). Use the split, on-demand loaders instead:
 * `useSupplierDynamicConfigLoader` + `useChatDynamicConfigLoader` (SRG-336).
 *
 * @param params - The loader parameters
 * @param params.businessOrganizationUUID - The business organization identifier
 * @param params.supplierUUID - The supplier identifier
 * @returns An object containing supplier config, chat config map, loading state, and error
 *
 * @example
 * ```typescript
 * const { chatDynamicConfigMap, loading, supplierDynamicConfig } =
 *   useSalesPortalDynamicConfigLoader({
 *     businessOrganizationUUID: 'org-123',
 *     supplierUUID: 'supplier-789',
 *   });
 *
 * // Access chat config by UUID
 * const chatConfig = chatDynamicConfigMap['chat-456'];
 * ```
 */
export const useSalesPortalDynamicConfigLoader = ({
  businessOrganizationUUID,
  supplierUUID,
}: SalesPortalDynamicConfigLoaderParams): SalesPortalDynamicConfigLoaderResult => {
  const { data, error, loading } = useGeneratedSalesPortalDynamicConfigQuery({
    fetchPolicy: 'cache-first',
    skip: !supplierUUID,
    variables: {
      businessOrganizationUUID,
      supplierUUID,
    },
  });

  // Cast required because GraphQL returns dynamic_config as Record<string, unknown>
  const supplierDynamicConfig = useMemo(
    () =>
      (data?.businesses_by_pk?.supplier_metadata?.dynamic_config ??
        null) as DynamicConfig | null,
    [data?.businesses_by_pk?.supplier_metadata?.dynamic_config],
  );

  const chatDynamicConfigMap = useMemo(() => {
    if (!data?.employee_chats) {
      return {};
    }
    return data.employee_chats.reduce<ChatDynamicConfigMap>((acc, chat) => {
      if (chat.chat_uuid) {
        // Cast required because GraphQL returns chat_dynamic_config as Record<string, unknown>
        acc[chat.chat_uuid] = (chat.chat_dynamic_config ??
          null) as DynamicConfig | null;
      }
      return acc;
    }, {});
  }, [data?.employee_chats]);

  return {
    chatDynamicConfigMap,
    error: error as Error | undefined,
    loading,
    supplierDynamicConfig,
  };
};
